import React from 'react';
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  closestCenter,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  CollisionDetection,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Case } from '../../types';

interface DragDropContextProps {
  children: React.ReactNode;
  onDragStart?: (event: DragStartEvent) => void;
  onDragOver?: (event: DragOverEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  activeCase?: Case | null;
  canDrop?: boolean;
  dropReason?: string;
}

export const ScheduleDragDropContext: React.FC<DragDropContextProps> = ({
  children,
  onDragStart,
  onDragOver,
  onDragEnd,
  activeCase,
  canDrop = true,
  dropReason = '',
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // カスタム衝突検出：陸送員ゾーンを最優先し、未割り当てエリアは最後の選択肢
  const customCollisionDetection: CollisionDetection = args => {
    // 複数の衝突検出アルゴリズムを試行
    const pointerCollisions = pointerWithin(args);
    const rectCollisions = rectIntersection(args);
    const closestCollisions = closestCenter(args);

    // 陸送員タイムスロットゾーンを最優先で検出
    const isTransporterZone = (collision: any) =>
      collision.id.startsWith('transporter-') &&
      collision.data?.droppableContainer?.data?.current?.type === 'transporter';

    // 1. ポインター位置での陸送員ゾーン検出を最優先
    const transporterPointer = pointerCollisions.filter(isTransporterZone);
    if (transporterPointer.length > 0) {
      return [transporterPointer[0]];
    }

    // 2. 矩形交差での陸送員ゾーン検出
    const transporterRect = rectCollisions.filter(isTransporterZone);
    if (transporterRect.length > 0) {
      return [transporterRect[0]];
    }

    // 3. 中心距離での陸送員ゾーン検出
    const transporterClosest = closestCollisions.filter(isTransporterZone);
    if (transporterClosest.length > 0) {
      return [transporterClosest[0]];
    }

    // 4. 陸送員ゾーンが見つからない場合のみ未割り当てゾーンを検出
    if (pointerCollisions.length > 0) {
      const unassignedPointer = pointerCollisions.filter(
        collision =>
          collision.id === 'unassigned-zone' ||
          collision.data?.droppableContainer?.data?.current?.type === 'unassigned'
      );
      if (unassignedPointer.length > 0) {
        return [unassignedPointer[0]];
      }
    }

    if (rectCollisions.length > 0) {
      const unassignedRect = rectCollisions.filter(
        collision =>
          collision.id === 'unassigned-zone' ||
          collision.data?.droppableContainer?.data?.current?.type === 'unassigned'
      );
      if (unassignedRect.length > 0) {
        return [unassignedRect[0]];
      }
    }

    if (closestCollisions.length > 0) {
      const unassignedClosest = closestCollisions.filter(
        collision =>
          collision.id === 'unassigned-zone' ||
          collision.data?.droppableContainer?.data?.current?.type === 'unassigned'
      );
      if (unassignedClosest.length > 0) {
        return [unassignedClosest[0]];
      }
    }

    return [];
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      {children}
      <DragOverlay>
        {activeCase && !canDrop ? (
          <div className='bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg px-3 py-2 shadow-lg opacity-95'>
            <div className='flex items-center text-red-800 dark:text-red-200 text-sm font-medium'>
              <span className='mr-2'>❌</span>
              <span>{dropReason || '割り当て不可'}</span>
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
